import type { ReactNode } from 'react';
import { useSelector } from 'react-redux';

import { Button } from '@interstate/components/Button';
import {
    CheckmarkSolidIcon,
    DetailsIcon,
    DocumentDuplicateIcon,
    LockClosedIcon,
    LockOpenIcon,
    TrashIcon
} from '@interstate/components/Icons';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { RadioButtonList } from '@interstate/components/RadioButtonList';
import { Tooltip } from '@interstate/components/Tooltip';
import { dealLockHooks, ManualTemplatesListSlideOut, useLockDeal, useManualTemplatesList } from '@makemydeal/dr-dash-components';
import { selectors as offerSelectors } from '@makemydeal/dr-offer-redux';

import { useMultiScenarioActions } from '../../baseDealActivity/baseDealContainer/useMultiScenarioActions';
import { ActionsGroup, ActionsRowRoot, PrimaryBadge, IconActionWrap, TemplateAppliedMark } from './ScenarioActionsRow.style';

export const SCENARIO_ACTION_LABELS = {
    primary: 'Primary',
    setPrimary: 'Set Primary',
    delete: 'Delete scenario',
    applyTemplate: 'Apply template',
    copy: 'Copy scenario',
    lock: 'Lock scenario',
    unlock: 'Unlock scenario'
} as const;

export const templateAppliedLabel = (templateName: string) => `Template applied: ${templateName}`;

type IconActionProps = {
    label: string;
    icon: ReactNode;
    onClick: () => void;
    disabled?: boolean;
    testId: string;
    adornment?: ReactNode;
};

const renderIconAction = ({ label, icon, onClick, disabled, testId, adornment }: IconActionProps) => (
    <Tooltip position="bottom" size="small" toolTipContent={label}>
        <IconActionWrap>
            <Button
                buttonStyle="tertiary"
                size="small"
                aria-label={label}
                icon={icon}
                onClick={onClick}
                disabled={disabled}
                data-testid={testId}
            />
            {adornment}
        </IconActionWrap>
    </Tooltip>
);

export const ScenarioActionsRow = ({ scopeId }: { scopeId: string }) => {
    const theme = useInterstateTheme();
    const { isPrimaryScope, isDeleteDisabled, isDuplicateDisabled, deleteScope, duplicateScope, makePrimary } =
        useMultiScenarioActions(scopeId);
    const lock = useLockDeal();
    const templates = useManualTemplatesList();
    const isDealLocked = dealLockHooks.useIsDealLocked();
    const isTemplateApplied = useSelector(offerSelectors.getIsTemplateApplied);
    const templateName = useSelector(offerSelectors.getTemplateName);

    const templateLabel = isTemplateApplied ? templateAppliedLabel(templateName) : SCENARIO_ACTION_LABELS.applyTemplate;
    const templateAppliedMark = isTemplateApplied && (
        <TemplateAppliedMark theme={theme} data-testid="scenario-template-applied-mark">
            <CheckmarkSolidIcon />
        </TemplateAppliedMark>
    );

    return (
        <ActionsRowRoot>
            {isPrimaryScope ? (
                <PrimaryBadge theme={theme} data-testid="scenario-primary-badge">
                    {SCENARIO_ACTION_LABELS.primary}
                </PrimaryBadge>
            ) : (
                <RadioButtonList
                    inline
                    displayLabel={false}
                    data-testid="scenario-set-primary"
                    options={[{ label: SCENARIO_ACTION_LABELS.setPrimary, value: scopeId, disabled: isDealLocked }]}
                    onChange={makePrimary}
                />
            )}
            <ActionsGroup $isPrimary={isPrimaryScope} theme={theme} data-testid="scenario-actions-group">
                {renderIconAction({
                    label: SCENARIO_ACTION_LABELS.delete,
                    icon: <TrashIcon />,
                    onClick: deleteScope,
                    disabled: isDeleteDisabled,
                    testId: 'scenario-delete'
                })}
                {renderIconAction({
                    label: templateLabel,
                    icon: <DetailsIcon />,
                    adornment: templateAppliedMark,
                    onClick: templates.open,
                    disabled: templates.isDisabled,
                    testId: 'scenario-apply-template'
                })}
                {renderIconAction({
                    label: SCENARIO_ACTION_LABELS.copy,
                    icon: <DocumentDuplicateIcon />,
                    onClick: duplicateScope,
                    disabled: isDuplicateDisabled,
                    testId: 'scenario-copy'
                })}
                {lock.isLockSupported &&
                    renderIconAction({
                        label: lock.isLocked ? SCENARIO_ACTION_LABELS.unlock : SCENARIO_ACTION_LABELS.lock,
                        icon: lock.isLocked ? <LockClosedIcon /> : <LockOpenIcon />,
                        onClick: lock.toggleLock,
                        disabled: lock.isDisabled,
                        testId: 'scenario-lock'
                    })}
            </ActionsGroup>
            <ManualTemplatesListSlideOut isOpen={templates.isOpen} onHide={templates.close} />
        </ActionsRowRoot>
    );
};
